import { useLayoutEffect, useRef, useState } from "react";
import type ReactSignatureCanvas from "react-signature-canvas";
import SignatureCanvas from "react-signature-canvas";
import { type TamaguiElement, Theme, XStack, YStack } from "tamagui";
import { FieldError } from "./FieldError";
import { TextButton } from "./LoadingButton";
import { SIGNATURE_CANVAS_HEIGHT, SIGNATURE_MAX_STROKE_WIDTH, SIGNATURE_MIN_STROKE_WIDTH, SIGNATURE_VELOCITY_FILTER_WEIGHT } from "./SignatureInput.constants";
import { Body2 } from "./Typography";
const SIGNATURE_CANVAS_MAX_WIDTH = 500;
interface Props {
  handleBegin?: () => void;
  handleEnd?: () => void;
  saveSignature: (signature: string | null) => void;
  error?: string | undefined;
  helperText?: string | undefined;
}
export function SignatureInput({
  handleBegin: handleBeginSignature,
  handleEnd: handleEndSignature,
  saveSignature,
  error,
  helperText
}: Props) {
  const ref = useRef<ReactSignatureCanvas>(null);
  const containerRef = useRef<TamaguiElement>(null);
  const [signatureWidth, setSignatureWidth] = useState<number>();
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container instanceof HTMLElement) {
      setSignatureWidth(Math.min(container.getBoundingClientRect().width, SIGNATURE_CANVAS_MAX_WIDTH));
    }
  }, []);
  const handleBegin = () => {
    if (handleBeginSignature) {
      handleBeginSignature();
    }
  };
  const handleEnd = () => {
    if (handleEndSignature) {
      handleEndSignature();
    }
    const signature = ref.current?.getTrimmedCanvas().toDataURL();
    if (signature) {
      saveSignature(signature);
    }
  };
  const handleClearSignature = () => {
    ref.current?.clear();
    saveSignature(null);
  };
  return <YStack ref={containerRef} width="100%" flex={1} ai="center" data-sentry-element="YStack" data-sentry-component="SignatureInput" data-sentry-source-file="SignatureInput.tsx">
      <YStack testID="hipaa.signature.canvas" height={SIGNATURE_CANVAS_HEIGHT} width={signatureWidth ?? "100%"} maxWidth={SIGNATURE_CANVAS_MAX_WIDTH} overflow="hidden" borderColor="$strokeSecondary" borderWidth="$0.5" br={10}
    // Cant import from local images so use the url directly,
    // backgroundImage is a web only prop so the .native version of this
    // file functions differently
    backgroundImage="url(/static/signatureBackground.png)" backgroundSize="contain" backgroundRepeat="no-repeat" backgroundPosition="center" data-sentry-element="YStack" data-sentry-source-file="SignatureInput.tsx">
        {signatureWidth !== undefined ? <SignatureCanvas
      // The fixed CSS width keeps the backing store in sync when the
      // surrounding layout changes without clearing existing strokes.
      canvasProps={{
        className: "sigCanvas",
        style: {
          width: signatureWidth,
          height: SIGNATURE_CANVAS_HEIGHT
        }
      }} clearOnResize={false} backgroundColor="transparent" ref={ref} onEnd={() => handleEnd()} onBegin={() => handleBegin()} minWidth={SIGNATURE_MIN_STROKE_WIDTH} maxWidth={SIGNATURE_MAX_STROKE_WIDTH} velocityFilterWeight={SIGNATURE_VELOCITY_FILTER_WEIGHT} /> : null}
      </YStack>
      <YStack pt="$sp0.5" data-sentry-element="YStack" data-sentry-source-file="SignatureInput.tsx">
        {error ? <Theme name="red" forceClassName>
            <FieldError message={error} />
          </Theme> : helperText ? <Body2 color="$textSubtle">{helperText}</Body2> : null}
      </YStack>
      <XStack justifyContent="center" pt="$4" $platform-web={{
      pb: "$4"
    }} data-sentry-element="XStack" data-sentry-source-file="SignatureInput.tsx">
        <TextButton height={30} gradientOff onPress={handleClearSignature} alignSelf="center" flexGrow={0} pressStyle={{
        backgroundColor: "transparent",
        scale: 0.97
      }} textProps={{
        hoverStyle: {
          color: "$textDefault"
        }
      }} data-sentry-element="TextButton" data-sentry-source-file="SignatureInput.tsx">
          Clear Signature
        </TextButton>
      </XStack>
    </YStack>;
}